import React from 'react';
import { DropResult } from '@hello-pangea/dnd';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  CircularProgress,
  Badge,
  Alert,
  useTheme
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Download as DownloadIcon,
  Rule as RuleIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Scenario } from '../../../types/models';

export interface ScenarioTableProps {
  scenarios: Scenario[];
  nextToken: string | null | undefined;
  loading: boolean;
  scenarioRuleCounts: Record<string, number>;
  formatDate: (dateString: string) => string;
  formatFullDateTime: (dateString: string) => string;
  onEdit: (scenario: Scenario) => void;
  onDelete: (scenario: Scenario) => void;
  onCustomEvaluation: (scenario: Scenario) => void;
  onDownload: (scenario: Scenario) => void;
  onToggleActive: (scenario: Scenario, checked: boolean) => void;
  onDragEnd: (result: DropResult) => void;
  onLoadMore: () => void;
  permCreateNew?: boolean;
  permUpdate?: boolean;
  permDelete?: boolean;
  permCustomScenarioUpdate?: boolean;
}

const ScenarioTable: React.FC<ScenarioTableProps> = ({
  scenarios,
  nextToken,
  loading,
  scenarioRuleCounts,
  formatDate,
  formatFullDateTime,
  onEdit,
  onDelete,
  onCustomEvaluation,
  onDownload,
  onToggleActive,
  onDragEnd,
  onLoadMore,
  permCreateNew = true,
  permUpdate = true,
  permDelete = true,
  permCustomScenarioUpdate = true,
}) => {
  const theme = useTheme();
  const linkBlue = theme.palette.smartSkillTalk?.linkBlue || '#1976d2';

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      {nextToken && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography component="span" variant="body2">
            並び替えは全件表示後にのみ可能です。「さらに表示する」で全件を表示してください。編集・公開状態の変更などはそのまま利用できます。
          </Typography>
        </Alert>
      )}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader aria-label="ロープレ一覧">
            <TableHead sx={{ bgcolor: '#F0F7FF' }}>
              <TableRow>
                <TableCell align="center" sx={{ color: linkBlue, fontSize: '13px', width: '30px' }} />
                <TableCell align="center" sx={{ color: linkBlue, fontSize: '13px' }}>作成日</TableCell>
                <TableCell align="center" sx={{ color: linkBlue, fontSize: '13px' }}>タイトル</TableCell>
                <TableCell align="center" sx={{ color: linkBlue, fontSize: '13px' }}>ロープレの概要文</TableCell>
                <TableCell align="center" sx={{ color: linkBlue, fontSize: '13px' }}>所属</TableCell>
                <TableCell align="center" sx={{ color: linkBlue, fontSize: '13px' }}>公開状態</TableCell>
                <TableCell align="center" sx={{ color: linkBlue, fontSize: '13px' }}>操作</TableCell>
              </TableRow>
            </TableHead>
            <Droppable droppableId="scenarios-list" isDropDisabled={!!nextToken}>
              {(provided) => (
                <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                  {scenarios.map((scenario: Scenario, index: number) => (
                    <Draggable
                      key={scenario.scenarioId}
                      draggableId={scenario.scenarioId}
                      index={index}
                      isDragDisabled={!!nextToken}
                    >
                      {(providedRow, snapshot) => (
                        <TableRow
                          ref={providedRow.innerRef}
                          {...providedRow.draggableProps}
                          hover
                          sx={{
                            bgcolor: index % 2 === 0 ? '#FFFFFF' : '#F9F9F9',
                            '&:hover': { bgcolor: '#F5F5F5' },
                            ...(snapshot.isDragging && {
                              bgcolor: '#E3F2FD',
                              boxShadow: '0 4px 8px rgba(0,0,0,0.12)'
                            })
                          }}
                        >
                          <TableCell
                            align="center"
                            sx={{
                              width: '30px',
                              padding: '8px',
                              ...(nextToken && { bgcolor: '#f5f5f5' })
                            }}
                          >
                            <Tooltip
                              title={nextToken ? '全件表示後に並び替え可能です' : 'ドラッグで並び替え'}
                              arrow
                            >
                              <Box
                                {...providedRow.dragHandleProps}
                                sx={{
                                  cursor: nextToken ? 'not-allowed' : 'grab',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  opacity: nextToken ? 0.5 : 1
                                }}
                              >
                                <DragIndicatorIcon
                                  sx={{ color: nextToken ? '#bbb' : '#999', fontSize: '18px' }}
                                />
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center" sx={{ color: '#333333', fontSize: '13px', letterSpacing: '0.05em' }}>
                            <Tooltip title={formatFullDateTime(scenario.createdAt)} arrow>
                              <span>{formatDate(scenario.createdAt)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="left" sx={{ color: '#333333', fontSize: '13px', letterSpacing: '0.05em' }}>
                            {scenario.title}
                          </TableCell>
                          <TableCell align="left" sx={{ color: '#333333', fontSize: '13px', letterSpacing: '0.05em' }}>
                            {scenario.description.length > 60
                              ? `${scenario.description.substring(0, 60)}...`
                              : scenario.description}
                          </TableCell>
                          <TableCell align="center" sx={{ color: '#333333', fontSize: '13px', letterSpacing: '0.05em' }}>
                            {scenario.department || '-'}
                          </TableCell>
                          <TableCell align="center">
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={scenario.isActive}
                                  onChange={(e) => onToggleActive(scenario, e.target.checked)}
                                  disabled={!(permCreateNew || permUpdate)}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: '#5EB600',
                                      '&:hover': { backgroundColor: 'rgba(94, 182, 0, 0.08)' }
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: '#5EB600'
                                    }
                                  }}
                                />
                              }
                              label=""
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                              {permUpdate && (
                                <Tooltip title="編集">
                                  <IconButton
                                    size="small"
                                    onClick={() => onEdit(scenario)}
                                    sx={{
                                      bgcolor: '#5D84AF',
                                      color: 'white',
                                      '&:hover': { bgcolor: '#5D84AF', opacity: 0.9 }
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {permCustomScenarioUpdate && (
                                <Tooltip title="カスタム評価">
                                  <IconButton
                                    size="small"
                                    onClick={() => onCustomEvaluation(scenario)}
                                    sx={{
                                      bgcolor: (scenarioRuleCounts[scenario.scenarioId] || 0) > 0 ? '#FF9800' : '#9E9E9E',
                                      color: 'white',
                                      '&:hover': {
                                        bgcolor: (scenarioRuleCounts[scenario.scenarioId] || 0) > 0 ? '#FF9800' : '#9E9E9E',
                                        opacity: 0.9
                                      }
                                    }}
                                  >
                                    <Badge
                                      badgeContent={
                                        (scenarioRuleCounts[scenario.scenarioId] || 0) > 0
                                          ? scenarioRuleCounts[scenario.scenarioId]
                                          : 0
                                      }
                                      color="error"
                                      sx={{
                                        '& .MuiBadge-badge': {
                                          right: -4,
                                          top: -4,
                                          minWidth: '18px',
                                          height: '18px',
                                          fontSize: '10px',
                                          fontWeight: 700
                                        }
                                      }}
                                    >
                                      <RuleIcon fontSize="small" />
                                    </Badge>
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="ダウンロード">
                                <IconButton
                                  size="small"
                                  onClick={() => onDownload(scenario)}
                                  sx={{
                                    bgcolor: '#5EB600',
                                    color: 'white',
                                    '&:hover': { bgcolor: '#5EB600', opacity: 0.9 }
                                  }}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {permDelete && (
                                <Tooltip title="削除">
                                  <IconButton
                                    size="small"
                                    onClick={() => onDelete(scenario)}
                                    sx={{
                                      bgcolor: '#F44336',
                                      color: 'white',
                                      '&:hover': { bgcolor: '#F44336', opacity: 0.9 }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
        </TableContainer>
      </Paper>
      {nextToken && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Button
            variant="contained"
            onClick={onLoadMore}
            disabled={loading}
            sx={{
              bgcolor: '#6C9AD7',
              color: 'white',
              fontSize: '16px',
              '&:hover': { bgcolor: '#6C9AD7', opacity: 0.9 }
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'さらに表示する'}
          </Button>
        </Box>
      )}
    </DragDropContext>
  );
};

export default React.memo(ScenarioTable);
